/**
 * Loads one picture at several resolutions in parallel.
 *
 * Belongs here: start the fetches, keep the decoded bitmaps, report the sharpest URL ready.
 * Does not belong here: the layout size. The caller draws every URL in the same box.
 */
import { useEffect, useState } from 'react'

export type ProgressiveImageSource = {
  src: string
  /** 1 is the smallest file. A later decode never replaces a sharper one. */
  scale: number
}

type ProgressiveImageSession = {
  bestScale: number
  bestSrc: string
  listeners: Set<(src: string) => void>
}

const sessions = new Map<string, ProgressiveImageSession>()
const decodedImages = new Map<string, HTMLImageElement>()

function sessionKey(sources: ProgressiveImageSource[]) {
  return sources.map((source) => `${source.scale}:${source.src}`).join('|')
}

function fetchPriorityForScale(scale: number, smallestScale: number, largestScale: number) {
  if (scale === smallestScale) return 'high'
  if (scale === largestScale) return 'low'
  return 'auto'
}

/** Start every resolution at once. Safe to call again with the same list. */
export function startProgressiveImage(sources: ProgressiveImageSource[]) {
  if (sources.length === 0 || typeof window === 'undefined') return
  const key = sessionKey(sources)
  if (sessions.has(key)) return

  const orderedSources = [...sources].sort((left, right) => left.scale - right.scale)
  const smallestScale = orderedSources[0].scale
  const largestScale = orderedSources[orderedSources.length - 1].scale
  const session: ProgressiveImageSession = {
    bestScale: 0,
    bestSrc: '',
    listeners: new Set()
  }
  sessions.set(key, session)

  for (const source of orderedSources) {
    const image = new Image()
    image.decoding = 'async'
    image.fetchPriority = fetchPriorityForScale(source.scale, smallestScale, largestScale)
    decodedImages.set(source.src, image)
    image.onload = () => {
      const promote = () => {
        // A slow small file must not cover a sharper one that already painted
        if (source.scale <= session.bestScale) return
        session.bestScale = source.scale
        session.bestSrc = source.src
        session.listeners.forEach((listener) => listener(source.src))
      }
      // Swap only after the bitmap is decoded, so the visible image does not go blank
      if (typeof image.decode === 'function') {
        image.decode().then(promote).catch(promote)
      } else {
        promote()
      }
    }
    image.src = source.src
  }
}

/** Current sharpest URL. Empty until the first resolution decodes. */
export function useProgressiveImage(sources: ProgressiveImageSource[]) {
  const key = sessionKey(sources)
  const [src, setSrc] = useState(() => sessions.get(key)?.bestSrc ?? '')

  useEffect(() => {
    startProgressiveImage(sources)
    const session = sessions.get(key)
    if (!session) return
    const showReadySrc = (nextSrc: string) => setSrc(nextSrc)
    session.listeners.add(showReadySrc)
    if (session.bestSrc) setSrc(session.bestSrc)
    return () => {
      session.listeners.delete(showReadySrc)
    }
  }, [key, sources])

  return src
}
